import { useState } from "react";
import { ScrollArea } from "@shared/components/ui/scroll-area";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@shared/components/ui/card";
import { Button } from "@shared/components/ui/button";
import { Badge } from "@shared/components/ui/badge";
import { Progress } from "@shared/components/ui/progress";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ThreeDotLoader from "@shared/components/ThreeDotLoader";
import {
  useCreateReturnVoucher,
  useNoReturnVoucherPackages,
} from "@features/return-voucher/hooks/useReturnVoucher";
import {
  Users,
  Package,
  CheckCircle2,
  XCircle,
  Clock,
  DollarSign,
  Phone,
  User,
  Undo2,
} from "lucide-react";

export default function CreateReturnVoucher() {
  const { t } = useTranslation();
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedPackages, setSelectedPackages] = useState([]);
  const { mutate, isPending } = useCreateReturnVoucher();
  const navigate = useNavigate();

  const { data, isLoading, error } = useNoReturnVoucherPackages();

  if (isLoading) return <ThreeDotLoader />;

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-[400px] text-center space-y-4">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
          <XCircle className="w-8 h-8 text-red-500" />
        </div>
        <div className="space-y-2">
          <h3 className="text-lg font-medium text-slate-900">
            {t("common.error")}
          </h3>
          <p className="text-slate-600">
            {error.message || t("common.errorLoadingData")}
          </p>
        </div>
      </div>
    );
  }

  const users = data?.data || [];

  const getStatusColor = (status) => {
    const colors = {
      delivered: "bg-green-50 text-green-700 border-green-200",
      pending: "bg-yellow-50 text-yellow-700 border-yellow-200",
      processing: "bg-blue-50 text-blue-700 border-blue-200",
      cancelled: "bg-red-50 text-red-700 border-red-200",
      returned: "bg-purple-50 text-purple-700 border-purple-200",
    };
    return colors[status?.toLowerCase()] || colors.cancelled;
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "delivered":
        return <CheckCircle2 className="w-3 h-3" />;
      case "pending":
        return <Clock className="w-3 h-3" />;
      case "processing":
        return <Package className="w-3 h-3" />;
      case "returned":
        return <Undo2 className="w-3 h-3" />;
      case "cancelled":
        return <XCircle className="w-3 h-3" />;
      default:
        return <Package className="w-3 h-3" />;
    }
  };

  const totalAmount = selectedUser
    ? selectedUser.packages
        .filter((pkg) => selectedPackages.includes(pkg._id))
        .reduce((sum, pkg) => sum + (pkg.totalAmount || 0), 0)
    : 0;

  const allSelected = selectedUser
    ? selectedPackages.length === selectedUser.packages.length
    : false;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedPackages([]);
    } else {
      setSelectedPackages(selectedUser.packages.map((pkg) => pkg._id));
    }
  };

  return (
    <div className="mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Undo2 className="w-8 h-8 text-primary" />
            {t("returnVoucher.generateVoucher")}
          </h1>
          <p className="text-slate-600">
            {t("returnVoucher.selectClientDescription") ||
              "Select a client and their packages to generate a return voucher"}
          </p>
        </div>

        {selectedUser && selectedPackages.length > 0 && (
          <div className="text-right space-y-1">
            <div className="text-sm text-slate-600">
              {selectedPackages.length} {t("packages.packages")}{" "}
              {t("common.selected")}
            </div>
            <div className="text-2xl font-bold text-primary flex items-center gap-1">
              <DollarSign className="w-5 h-5" />
              {totalAmount.toFixed(2)} {t("common.currency")}
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Users List */}
        <Card className="lg:col-span-4 shadow-lg border-0 bg-white/80 backdrop-blur-sm">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Users className="w-5 h-5 text-primary" />
              {t("navigation.users")}
              <Badge variant="secondary" className="ml-auto">
                {users.length}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[500px]">
              {users.length > 0 ? (
                <div className="space-y-2 p-4">
                  {users.map((user) => (
                    <div
                      key={user.userId}
                      className={`group relative p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 hover:shadow-md ${
                        selectedUser?.userId === user.userId
                          ? "border-primary bg-primary/5 shadow-md"
                          : "border-slate-200 hover:border-slate-300 bg-white"
                      }`}
                      onClick={() => {
                        setSelectedUser(user);
                        setSelectedPackages([]);
                      }}
                    >
                      <div className="flex items-start justify-between">
                        <div className="space-y-2 flex-1">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-slate-600" />
                            <h3 className="font-semibold text-slate-900 group-hover:text-primary transition-colors">
                              {user.brandName}
                            </h3>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <Package className="w-3 h-3" />
                            {user.packages.length} {t("packages.packages")}
                          </div>
                        </div>
                        {selectedUser?.userId === user.userId && (
                          <CheckCircle2 className="w-5 h-5 text-primary" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-[500px] text-center space-y-4">
                  <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center">
                    <Users className="w-8 h-8 text-slate-400" />
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-lg font-medium text-slate-900">
                      {t("returnVoucher.noClientsAvailable")}
                    </h3>
                    <p className="text-slate-600 max-w-sm">
                      {t("returnVoucher.noPackagesForVoucher")}
                    </p>
                  </div>
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Packages List */}
        <Card className="lg:col-span-8 shadow-lg border-0 bg-white/80 backdrop-blur-sm">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Package className="w-5 h-5 text-primary" />
                {selectedUser
                  ? `${selectedUser.brandName} - ${t("packages.packages")}`
                  : t("returnVoucher.selectClient")}
                {selectedUser && (
                  <Badge variant="secondary">
                    {selectedUser.packages.length}
                  </Badge>
                )}
              </CardTitle>

              {selectedUser && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={toggleSelectAll}
                    className="gap-2"
                  >
                    {allSelected ? (
                      <CheckCircle2 className="w-4 h-4" />
                    ) : (
                      <Package className="w-4 h-4" />
                    )}
                    {allSelected
                      ? t("common.deselectAll")
                      : t("common.selectAll")}
                  </Button>

                  <Button
                    disabled={isPending || selectedPackages.length === 0}
                    onClick={() => {
                      if (
                        !selectedUser?.userId ||
                        selectedPackages.length === 0
                      ) {
                        toast.error(t("returnVoucher.selectPackagesError"));
                        return;
                      }

                      mutate(
                        {
                          client: selectedUser.userId,
                          packages: selectedPackages,
                        },
                        {
                          onSuccess: () => {
                            toast.success(t("returnVoucher.voucherGenerated"));
                            setSelectedPackages([]);
                            setSelectedUser(null);
                            setTimeout(() => {
                              navigate("/return-voucher");
                            }, 500);
                          },
                          onError: (err) => {
                            toast.error(
                              err?.message || t("common.errorOccurred")
                            );
                          },
                        }
                      );
                    }}
                    className="gap-2 min-w-[140px]"
                  >
                    {isPending ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        {t("returnVoucher.generatingVoucher")}
                      </>
                    ) : (
                      <>
                        <Undo2 className="w-4 h-4" />
                        {t("returnVoucher.generateVoucher")}
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>

            {selectedUser && selectedPackages.length > 0 && (
              <div className="mt-4">
                <Progress
                  value={
                    (selectedPackages.length / selectedUser.packages.length) *
                    100
                  }
                  className="h-2"
                />
                <div className="flex justify-between text-xs text-slate-600 mt-1">
                  <span>
                    {selectedPackages.length} {t("common.of")}{" "}
                    {selectedUser.packages.length} {t("packages.packages")}
                  </span>
                  <span>
                    {(
                      (selectedPackages.length / selectedUser.packages.length) *
                      100
                    ).toFixed(0)}
                    %
                  </span>
                </div>
              </div>
            )}
          </CardHeader>

          <CardContent className="p-0">
            {selectedUser ? (
              <ScrollArea className="h-[500px]">
                <div className="space-y-3 p-4">
                  {selectedUser.packages.map((pkg) => {
                    const isSelected = selectedPackages.includes(pkg._id);
                    return (
                      <div
                        key={pkg._id}
                        className={`group p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 hover:shadow-md ${
                          isSelected
                            ? "border-primary bg-primary/5 shadow-md"
                            : "border-slate-200 hover:border-slate-300 bg-white"
                        }`}
                        onClick={() => {
                          setSelectedPackages((prevSelected) => {
                            if (prevSelected.includes(pkg._id)) {
                              return prevSelected.filter(
                                (id) => id !== pkg._id
                              );
                            } else {
                              return [...prevSelected, pkg._id];
                            }
                          });
                        }}
                      >
                        <div className="flex items-start justify-between">
                          <div className="space-y-3 flex-1">
                            <div className="flex items-center gap-3">
                              <div
                                className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                                  isSelected
                                    ? "bg-primary border-primary text-white"
                                    : "border-gray-300 bg-white"
                                }`}
                              >
                                {isSelected && (
                                  <CheckCircle2 className="w-3 h-3" />
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <Phone className="w-4 h-4 text-slate-600" />
                                <span className="font-medium text-slate-900">
                                  {pkg.phone || "N/A"}
                                </span>
                              </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                              <div className="flex items-center gap-2">
                                <User className="w-3 h-3 text-slate-500" />
                                <span className="text-slate-600">
                                  {pkg.customerName || "N/A"}
                                </span>
                              </div>

                              <div className="flex items-center gap-2">
                                <Badge
                                  variant="outline"
                                  className={`gap-1 ${getStatusColor(
                                    pkg.status
                                  )}`}
                                >
                                  {getStatusIcon(pkg.status)}
                                  {t("status." + (pkg.status || "pending"))}
                                </Badge>
                              </div>

                              <div className="flex items-center gap-2 justify-end">
                                <DollarSign className="w-3 h-3 text-slate-500" />
                                <span className="font-semibold text-slate-900">
                                  {pkg.totalAmount || 0} {t("common.currency")}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            ) : (
              <div className="flex flex-col items-center justify-center h-[500px] text-center space-y-4">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center">
                  <Users className="w-8 h-8 text-slate-400" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg font-medium text-slate-900">
                    {t("returnVoucher.noClientSelected")}
                  </h3>
                  <p className="text-slate-600 max-w-sm">
                    {t("returnVoucher.selectClientToViewPackages")}
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Summary Bar */}
      {selectedUser && selectedPackages.length > 0 && (
        <Card className="shadow-lg border-0 bg-white/90 backdrop-blur-sm border-t-4 border-t-primary">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <h3 className="text-lg font-semibold text-slate-900">
                  {t("returnVoucher.voucherSummary")}
                </h3>
                <p className="text-slate-600">
                  {selectedPackages.length} {t("packages.packages")}{" "}
                  {t("common.selected")} {t("common.for")}{" "}
                  {selectedUser.brandName}
                </p>
              </div>

              <div className="text-right space-y-1">
                <div className="text-sm text-slate-600">
                  {t("common.totalAmount")}
                </div>
                <div className="text-3xl font-bold text-primary flex items-center gap-1">
                  <DollarSign className="w-6 h-6" />
                  {totalAmount.toFixed(2)} {t("common.currency")}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
